import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import TwoColumnL30R70 from '@klaviyo-mkt/gatsby-components/utils/two-column-l30-r70';
import Typography from '@klaviyo-mkt/gatsby-components/atoms/typography';

import GlobalHead from '../../components/head';
import Layout from '../../layout';
import * as s from './styles';

import type { HeadProps, PageProps } from 'gatsby';
import type { IPageContext } from '../../components/head/types';
import type { IHCLayout } from '../../layout/types';

export const Section = ({ data: { section, childSections }, location, ...pageProps }: PageProps<Queries.SectionQuery>) => {
  const { t } = useTranslation();

  if (section === null) {
    return null;
  }

  const {
    articles = [],
  } = section;
  
  const compareArticlePosition = (a: { position: number | null; } | null, b: { position: number | null; } | null) => (b != null && a !== null && a.position !== null && b.position !== null) ? (a.position > b.position ? 1 : -1) : 0;
  const articlesExist = articles !== null && articles?.length > 0;
  const sortedSubArticles = articlesExist ? [...articles].sort(compareArticlePosition) : [];

  return (
    <Layout
      {...pageProps}
      location={location}
      translations={section?.translations?.filter(Boolean) as IHCLayout['translations']}
    >
      <s.SectionPage>
        <TwoColumnL30R70
          headline={t('helpCenter_templates_home_twoColumnL30R70_headline')}
          childrenLeft={<s.StyledTopicsMenu selectedTopic={section?.category?.name || undefined} />}
          childrenRight={(
            <s.Content>
              <s.SectionMeta>
                <s.SectionName colorVariant="dark" variant="h5Semibold">
                  {(typeof section?.category?.sourceId === 'string') && (
                    <>
                      <s.ParentLink
                        to={`/hc/${section?.locale}/categories/${section.category.sourceId}`}
                      >
                        {section.category.name}
                      </s.ParentLink>
                      <span> / </span>
                    </>
                  )}
                  {(section?.parentSection !== null && typeof section?.parentSection?.sourceId === 'string') && (
                    <>
                      <s.ParentLink
                        to={`/hc/${section?.locale}/sections/${section?.parentSection?.sourceId}`}
                      >
                        {section.parentSection.name}
                      </s.ParentLink>
                      <span> / </span>
                    </>
                  )}
                  <h1>{section.name}</h1>
                </s.SectionName>
                <s.SectionDescription colorVariant="dark" variant="h6">{section.description}</s.SectionDescription>
              </s.SectionMeta>
              {(childSections !== null && childSections?.nodes.length > 0) && (
                <s.Articles>
                  {childSections.nodes.map((childSection) => {
                    const sortedChildArticles = childSection.articles  && childSection.articles.length > 0 ? [...childSection.articles].sort(compareArticlePosition) : [];
                                      
                    return (
                      <s.SubSectionContent key={childSection.id}>
                        <Typography variant='h6Semibold'>
                          <s.SubSectionTitleLink to={`/hc/${section?.locale}/sections/${childSection.sourceId}`}>{childSection.name}</s.SubSectionTitleLink>
                        </Typography>
                        {sortedChildArticles.map((article) => (
                          <s.ArticleLink to={`/hc/${section?.locale}/articles/${article?.sourceId}`} key={article?.sourceId}>{article?.name}</s.ArticleLink>
                        ))}
                      </s.SubSectionContent>
                    );
                  })}
                </s.Articles>
              )}
              {(articlesExist) && (
                <s.Articles>
                  {sortedSubArticles
                    .map((article) => (
                      <s.ArticleLink to={`/hc/${section?.locale}/articles/${article?.sourceId}`} key={article?.sourceId}>{article?.name}</s.ArticleLink>
                    ))}
                </s.Articles>
              )}
            </s.Content>
          )}
        />
      </s.SectionPage>
    </Layout >
  );
};

export default Section;

export const Head = ({ data: { section }, location, pageContext }: HeadProps<Queries.SectionQuery, IPageContext>) => (
  <GlobalHead
    langCode={pageContext?.locale}
    pathname={location.pathname}
    name={section?.name || ''}
    pageType="category"
    seo={{
      canonical: `https://help.klaviyo.com/hc/${section?.locale}/sections/${section?.sourceId}`,
    }}
    translations={section?.translations?.filter(Boolean) as IHCLayout['translations']}
  />
);

export const query = graphql`
  query Section($id: String, $locale: String) {
    section(id: { eq: $id }) {
      articles {
        name
        id
        locale
        position
        slug
        section {
          id
          locale
          parentSection {
            sourceId
          }
        }
        sourceId
      }
      id
      name
      description
      htmlUrl
      position
      draft
      locale
      category {
        name
        id
        slug
        sourceId
      }
      parentSection {
        id
        name
        slug
        sourceId
      }
      sourceId
      translations {
        hreflang
        locale
        name
        url
      }
    }
    childSections: allSection(
      filter: {
        parentSection: {id: {eq: $id}},
        locale: {eq: $locale}
        }
      sort: {position: ASC}
    ) {
      nodes {
        id
        sourceId
        articles {
          id
          name
          locale
          position
          slug
          sourceId
        }
        locale
        name
        slug
      }
    }
  }
`;
